// 임베드 서비스(Nextcloud·Mattermost·Roundcube) 인증 만료 — 자동 재인증 1회 + 원인 안내 공통 모듈.
// 설계·트리거·수치: docs/embed_auth_expiry.md ⑥.
// 🔴 여기서는 저장소(localStorage·sessionStorage)에 아무것도 쓰지 않는다 — 재인증 상한(guard)과 보고 상한은
//    메모리에만 둔다. 토큰은 읽기만 한다(게이트웨이 토큰 저장은 client.js resumeSession 한 곳뿐).
// 🔴 client·ncSso 를 일부만 목킹한 시험이 많다 — 가져온 함수는 부르기 전에 typeof 로 확인한다(App.jsx:136 관례).
import { probeNcSessionState } from './ncSso';
import { isNcSessionDead } from '../utils/embedCollab';
import { resumeSession } from '../api/client';
import { logoutGateway } from './gatewayLogout';

// 앱 키 → 인증 서비스. NC 앱은 세션 쿠키 하나를 같이 쓰므로 재인증 상한도 서비스 단위로 센다.
export const EMBED_SERVICE = {
  drive: 'nextcloud',
  calendar: 'nextcloud',
  mail: 'nextcloud',
  talk: 'nextcloud',
  collabtools: 'nextcloud',
  office: 'nextcloud',
  messenger: 'mattermost',
  mail2: 'roundcube',
};

export function serviceOf(app) {
  return Object.prototype.hasOwnProperty.call(EMBED_SERVICE, app) ? EMBED_SERVICE[app] : null;
}

// ── 자동 재인증 상한(guard) ────────────────────────────────────────────────
// 서비스별 60초 쿨다운 + 10분 3회 상한. 🔴 자동 재인증이 한 번 실패하면(fail) 사용자가 «다시 시도» 를
// 눌러 reset 하기 전까지 자동으로 다시 시도하지 않는다. succeed 는 실패 표식만 지운다(쿨다운·상한은 그대로).
export function createReauthGuard({ now = () => Date.now(), cooldownMs = 60000, windowMs = 600000, max = 3 } = {}) {
  const state = {};
  const entry = (key) => state[key] || (state[key] = { last: 0, times: [], failed: false });
  // 막힌 이유(부르기만 해서는 아무것도 바뀌지 않는다): null | 'failed' | 'cooldown' | 'limit'
  const check = (key) => {
    const s = state[key];
    if (!s) return null;
    if (s.failed) return 'failed';
    const t = now();
    if (s.last && t - s.last < cooldownMs) return 'cooldown';
    if (s.times.filter((x) => t - x < windowMs).length >= max) return 'limit';
    return null;
  };
  return {
    check,
    tryAcquire(key) {
      if (check(key)) return false;
      const s = entry(key);
      const t = now();
      s.times = s.times.filter((x) => t - x < windowMs);
      s.last = t;
      s.times.push(t);
      return true;
    },
    fail(key) { entry(key).failed = true; },
    succeed(key) { if (state[key]) state[key].failed = false; },
    reset(key) { delete state[key]; },
    resetAll() { Object.keys(state).forEach((k) => { delete state[k]; }); },
  };
}

// 탭 하나에 하나 — EmbedHost·EmbedPage·CollabViewPage 가 같이 쓴다.
export const reauthGuard = createReauthGuard();

// ── NC 복구 단일 비행(탭 안) ──────────────────────────────────────────────
// 🔴 NC 세션을 되살리는 길은 여럿이다 — 열기 직전 확인(EmbedHost connectNcApp)·복귀 확인(resumeAuthCheck)·
//    화면의 재인증(EmbedPage onLoad·포커스·다시 열기·5초 감시 신호). NC 앱들은 세션 쿠키 하나를 같이 쓰므로
//    이 길들이 동시에 돌면 서로의 쿠키 정리(nc-session-reset)·OIDC state 를 덮어써 SSO 가 깨지고, 그 실패가
//    또 다른 복구를 부른다. 그래서 지금 도는 NC 복구를 하나 기록해 두고, 나머지 길은 새로 띄우지 않고
//    (guard 도 쓰지 않고) 그 결과를 기다린다.
// 🔴 메모리에만 둔다. 60초 넘게 안 끝난 기록은 버린다(멈춘 약속이 복구를 영영 막지 않게 —
//    ensureNcSession 은 20초 상한 + 확인 8초라 정상이면 그 안에 끝난다).
const NC_RECOVERY_STALE_MS = 60000;
let ncRecovery = null;                   // { promise, at } — 지금 도는 NC 복구
let ncReloadClaims = new WeakMap();      // 복구(약속) → 그 복구 뒤 이미 다시 불러온 앱들
const ncRecoveredListeners = new Set();

// 지금 도는 NC 복구(약속) 또는 null.
export function currentNcRecovery() {
  if (ncRecovery && Date.now() - ncRecovery.at > NC_RECOVERY_STALE_MS) ncRecovery = null;
  return ncRecovery ? ncRecovery.promise : null;
}

// ensureNcSession 의 결과를 지금 도는 NC 복구로 기록한다(이미 다른 복구가 돌고 있으면 기록만 건너뛴다).
// 반환 = 그 결과의 Promise — 호출부가 여기에 then 을 건다(기록 쪽 then 이 먼저 돈다).
// 숨은 SSO 로 세션이 새로 생기면(reauthed) 구독자에게 알린다 — 그때 떠 있던 NC 문서는 전부 옛 문서다.
export function trackNcRecovery(result) {
  const promise = Promise.resolve(result);
  if (currentNcRecovery()) return promise;
  const entry = { promise, at: Date.now() };
  ncRecovery = entry;
  promise.then((session) => {
    if (ncRecovery === entry) ncRecovery = null;
    if (session && session.reauthed) {
      ncRecoveredListeners.forEach((listener) => {
        try { listener(promise, session); } catch (e) { /* 구독자 오류가 다른 구독자를 막지 않게 */ }
      });
    }
  }, () => { if (ncRecovery === entry) ncRecovery = null; });
  return promise;
}

// 복구 하나 뒤 앱마다 다시 불러오기는 한 번 — 처음 묻는 쪽만 true(EmbedHost 의 재로드 신호와 화면 자신의
// 재로드가 겹치지 않게). 약속이 아닌 값(null)은 묶을 복구가 없다는 뜻이라 늘 true.
export function claimNcReload(promise, app) {
  if (!promise || typeof promise !== 'object') return true;
  let apps = ncReloadClaims.get(promise);
  if (!apps) {
    apps = new Set();
    ncReloadClaims.set(promise, apps);
  }
  if (apps.has(app)) return false;
  apps.add(app);
  return true;
}

// 숨은 SSO 로 NC 세션이 새로 생겼을 때(reauthed) listener(promise, session) 를 부른다. 반환 = 해제 함수.
export function subscribeNcRecovered(listener) {
  if (typeof listener !== 'function') return () => {};
  ncRecoveredListeners.add(listener);
  return () => { ncRecoveredListeners.delete(listener); };
}

// 로그아웃·로그인 화면 진입(EmbedHost reset) 때 비운다.
export function resetNcRecovery() {
  ncRecovery = null;
  ncReloadClaims = new WeakMap();
}

// ── 세션 확인(probe) ──────────────────────────────────────────────────────
const UNREACHABLE = { state: 'unreachable', uid: null };

// NC 세션 상태: {state:'ok'|'none'|'unreachable', uid}. ncSso 를 목킹해 함수가 없으면 'unreachable'
// (= 아무것도 하지 않는다 — 일시 장애를 미리 경고하지 않는 것과 같은 결론).
export async function probeNcState(options) {
  if (typeof probeNcSessionState !== 'function') return UNREACHABLE;
  try {
    const r = await probeNcSessionState(options);
    return r && r.state ? r : UNREACHABLE;
  } catch (e) {
    return UNREACHABLE;
  }
}

const MM_ME_URL = '/mm/api/v4/users/me';

// MM 세션 상태: 200 'ok' · 401 'expired' · 네트워크·타임아웃·502~504 'unreachable' · 그 밖 'other'.
export async function probeMmState({ timeoutMs = 8000 } = {}) {
  if (typeof fetch !== 'function') return 'unreachable';
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => { try { controller.abort(); } catch (e) { /* */ } }, timeoutMs) : null;
  try {
    const init = { credentials: 'include' };
    if (controller) init.signal = controller.signal;
    const r = await fetch(MM_ME_URL, init);
    const status = r && r.status;
    if (status === 200) return 'ok';
    if (status === 401) return 'expired';
    if (status === 502 || status === 503 || status === 504) return 'unreachable';
    return 'other';
  } catch (e) {
    return 'unreachable';
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// ── iframe 문서 판정 ──────────────────────────────────────────────────────
// 게이트웨이 로그인 화면(`/login`)이 iframe 안에 떴다 = 게이트웨이 토큰이 없거나 만료된 채 SSO 를 탔다
// (AuthorizePage 가 `/login?next=` 로 보낸다).
const GATEWAY_LOGIN_RE = /^\/login(\/|$)/;
const MM_LOGIN_RE = /^\/mm\/login(\/|$)/;
// SSO·인가 중간 문서 — 체인이 이어지는 동안 잠깐 지나간다(게이트웨이 `/authorize` 브리지 · NC user_oidc ·
// MM OAuth). 로그인 튕김·차단으로 보지 않는다(5초 감시·onLoad 재인증이 반응하지 않게).
const SSO_INTERMEDIATE_RE = /^\/authorize(\/|$)|\/apps\/user_oidc\/|^\/mm\/oauth\/|^\/mm\/signup\/gitlab\//;

export function isSsoIntermediatePath(path) {
  return SSO_INTERMEDIATE_RE.test(String(path || ''));
}

// 같은 출처일 때만 읽는다 — 읽다 예외가 나면(교차 출처·차단) 로그인 문서로 보지 않는다.
export function isLoginDocument(app, win) {
  if (!win) return false;
  let path = '';
  try {
    path = String((win.location && win.location.pathname) || '');
  } catch (e) {
    return false;
  }
  if (GATEWAY_LOGIN_RE.test(path)) return true;
  const service = serviceOf(app);
  if (service === 'nextcloud') {
    // 🔴 SSO 중간 경로(`/drive/apps/user_oidc/login/1`)도 isNcSessionDead 정규식에 걸린다 — 빼고 본다.
    if (path.indexOf('/apps/user_oidc/') !== -1) return false;
    return isNcSessionDead(win) === true;
  }
  if (service === 'mattermost') return MM_LOGIN_RE.test(path);
  if (service === 'roundcube') {
    // RC 로그인 화면은 경로가 `/mail2/` 그대로라 경로로는 못 가린다 — 문서를 본다.
    try {
      const rc = win.rcmail;
      if (rc && rc.env && rc.env.task === 'login') return true;
    } catch (e) { /* */ }
    try {
      const doc = win.document;
      if (doc && typeof doc.getElementById === 'function' && doc.getElementById('rcmloginuser')) return true;
      const body = doc && doc.body;
      if (body && body.classList && typeof body.classList.contains === 'function' && body.classList.contains('task-login')) return true;
    } catch (e) { /* */ }
    return false;
  }
  return false;
}

function hasSrcAttr(iframe) {
  try {
    return typeof iframe.getAttribute === 'function' && !!iframe.getAttribute('src');
  } catch (e) {
    return false;
  }
}

// iframe 이 지금 어떤 상태인가: null | 'login_redirect' | 'gateway_login' | 'blocked_frame'.
// 'blocked_frame' 은 load 직후(afterLoad)이고 src 가 있는데 location 을 못 읽거나 about:blank 일 때만이다
// (frame-ancestors 차단·오류 페이지). 주기 감시(afterLoad:false)는 로그인 튕김만 본다.
export function detectFrameProblem(iframe, app, { afterLoad = false } = {}) {
  if (!iframe) return null;
  let win = null;
  let href = '';
  let path = '';
  try {
    win = iframe.contentWindow;
    const loc = win && win.location;
    if (loc) {
      href = loc.href == null ? '' : String(loc.href);
      path = loc.pathname == null ? '' : String(loc.pathname);
    }
  } catch (e) {
    return afterLoad && hasSrcAttr(iframe) ? 'blocked_frame' : null;
  }
  if (!win) return null;
  if (afterLoad && hasSrcAttr(iframe) && href === 'about:blank') return 'blocked_frame';
  if (isSsoIntermediatePath(path)) return null;
  if (GATEWAY_LOGIN_RE.test(path)) return 'gateway_login';
  if (isLoginDocument(app, win)) return 'login_redirect';
  return null;
}

// ── 게이트웨이 토큰 ──────────────────────────────────────────────────────
// JWT exp(ms). 못 읽으면 null — App.jsx hasLiveToken 과 같이 «만료 시각을 모르면 살아 있다» 로 본다.
function tokenExpMs(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const exp = Number(claims && claims.exp);
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

function readToken() {
  try {
    return localStorage.getItem('token');
  } catch (e) {
    return null;
  }
}

// SSO 를 띄우기 전에 게이트웨이 토큰이 SSO 브리지(/authorize)를 통과할 수 있는지 본다.
// 토큰 없음 = false · 만료까지 marginMs 넘게 남음 = true · 아니면 resume(탭 안 single-flight) 결과.
// 🔴 resume 은 부모 창에서만 부른다 — iframe 안(AuthorizePage)에서 부르면 refresh 쿠키를 동시에 회전시켜
//    family 전체가 폐기된다.
export async function ensureGatewayToken({ marginMs = 60000 } = {}) {
  const token = readToken();
  if (!token) return false;
  const exp = tokenExpMs(token);
  if (!exp || exp - Date.now() > marginMs) return true;
  if (typeof resumeSession !== 'function') return false;
  try {
    const out = await resumeSession();
    return !!(out && out.ok);
  } catch (e) {
    return false;
  }
}

// ── 실패 원인 ────────────────────────────────────────────────────────────
// 실패 카드 둘째(셋째) 줄 — 원인 종류마다 한 줄.
export const CAUSE_TEXT = {
  auth_expired: '서비스 로그인이 만료됐고 자동으로 다시 연결하지 못했습니다.',
  server_unreachable: '서비스 서버가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.',
  gateway_session: '게이트웨이 로그인이 만료됐습니다. 다시 로그인해 주세요.',
  blocked_frame: '브라우저가 이 화면 표시를 막았습니다. 다시 시도하거나 다시 로그인해 주세요.',
  cooldown: '자동 재연결을 잠시 멈췄습니다. «다시 시도» 를 눌러 주세요.',
  unknown: '연결 중 알 수 없는 오류가 났습니다. 다시 시도해 주세요.',
};

export function causeText(cause) {
  return CAUSE_TEXT[cause] || CAUSE_TEXT.unknown;
}

function ncSsoCode(err) {
  return err && err.name === 'NcSsoError' && typeof err.code === 'string' ? err.code : null;
}

// ensureNcSession 실패 → 원인 종류.
export function classifyEmbedError(err) {
  const code = ncSsoCode(err);
  if (code === 'unreachable') return 'server_unreachable';
  if (code === 'gateway_session' || code === 'missing_expected_user') return 'gateway_session';
  if (code === 'timeout' || code === 'uid_mismatch') return 'auth_expired';
  return 'unknown';
}

// ensureNcSession 실패 → 보고 reason(목록 값).
export function embedErrorReason(err) {
  const code = ncSsoCode(err);
  if (code === 'timeout') return 'timeout';
  if (code === 'uid_mismatch') return 'uid_mismatch';
  if (code === 'unreachable') return 'server_unreachable';
  if (code === 'gateway_session' || code === 'missing_expected_user') return 'gateway_session';
  return 'other';
}

// 원인 종류 → 보고 reason(목록 값).
const CAUSE_REASON = {
  auth_expired: 'session_missing',
  server_unreachable: 'server_unreachable',
  gateway_session: 'gateway_session',
  blocked_frame: 'blocked_frame',
  cooldown: 'cooldown',
  unknown: 'other',
};

export function causeReason(cause) {
  return CAUSE_REASON[cause] || 'other';
}

// ── 보고(POST /api/collab/embed-auth-event) ────────────────────────────────
// 백엔드 EmbedAuthEvent(routers/collab_session.py)의 Literal 목록과 같은 값이다.
const REPORT_SERVICES = ['nextcloud', 'mattermost', 'roundcube'];
const REPORT_EVENTS = ['expired', 'reauth_ok', 'reauth_fail'];
export const EMBED_AUTH_REASONS = [
  'open_check', 'visibility_check', 'login_redirect', 'blocked_frame', 'session_missing',
  'timeout', 'uid_mismatch', 'server_unreachable', 'gateway_session', 'cooldown',
  'manual_retry', 'other',
];
const REPORT_WINDOW_MS = 600000;
const REPORT_MAX_IN_WINDOW = 20;
let reportTimes = [];

// 본문은 service·event·reason 세 값뿐이다. 실패는 무시한다(그 API 가 없어도 동작은 같다).
// 🔴 절대 예외를 던지지 않는다. 반환 = 보냈는지 여부.
export function reportEmbedAuthEvent(service, event, reason) {
  try {
    if (REPORT_SERVICES.indexOf(service) === -1 || REPORT_EVENTS.indexOf(event) === -1) return false;
    const safeReason = EMBED_AUTH_REASONS.indexOf(reason) === -1 ? 'other' : reason;
    if (typeof fetch !== 'function') return false;
    const token = readToken();
    if (!token) return false;
    const t = Date.now();
    reportTimes = reportTimes.filter((x) => t - x < REPORT_WINDOW_MS);
    if (reportTimes.length >= REPORT_MAX_IN_WINDOW) return false;
    reportTimes.push(t);
    const api = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) || '';
    const sent = fetch(`${api}/api/collab/embed-auth-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ service, event, reason: safeReason }),
    });
    if (sent && typeof sent.catch === 'function') sent.catch(() => {});
    return true;
  } catch (e) {
    return false;
  }
}

export function resetEmbedAuthForTests() {
  reportTimes = [];
  reauthGuard.resetAll();
  resetNcRecovery();
}

// ── 메일2(Roundcube) 자동 로그인 티켓 ────────────────────────────────────────
// 반환 {ready, reason} · 네트워크 오류·502~504 는 null(= 서버가 응답하지 않는다).
// 티켓은 HttpOnly 쿠키로만 온다 — 스크립트는 '가능한가' 만 안다(비밀번호는 브라우저를 거치지 않는다).
export async function issueMail2Ticket() {
  const token = readToken();
  if (!token || typeof fetch !== 'function') return null;
  const api = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) || '';
  let r = null;
  try {
    r = await fetch(`${api}/api/mail2/autologin`, {
      method: 'POST', credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    return null;
  }
  const status = r && r.status;
  if (status === 502 || status === 503 || status === 504) return null;
  if (!r || !r.ok) return { ready: false, reason: status ? `http_${status}` : 'error' };
  let body = null;
  try {
    body = typeof r.json === 'function' ? await r.json() : null;
  } catch (e) {
    body = null;
  }
  return { ready: !!(body && body.ready === true), reason: (body && typeof body.reason === 'string') ? body.reason : '' };
}

// ── 다시 로그인 ──────────────────────────────────────────────────────────
// 기존 로그아웃 절차(logoutGateway) 뒤 /login 으로. 🔴 useNavigate 를 쓰지 않는다 — EmbedContent·
// OfficePage 시험이 Router 없이 렌더한다.
export async function relogin() {
  try {
    if (typeof logoutGateway === 'function') await logoutGateway();
  } catch (e) { /* 로컬 자격증명은 이미 지워졌다 */ }
  try {
    window.location.assign('/login');
  } catch (e) { /* jsdom 은 이동을 구현하지 않는다 */ }
}
